import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Building2,
  Users,
  Plus,
  Search,
  Phone,
  Mail,
  MapPin,
  Star,
  Edit,
  Trash2,
  Filter,
  Download,
  Eye,
  X
} from 'lucide-react';
import api from '../api/config';
import BusinessPartnerModal from '../components/BusinessPartnerModal';
import './ClientsPage.css';

const ClientsPage = () => {
  const location = useLocation();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [openContactsDirectly, setOpenContactsDirectly] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [statistics, setStatistics] = useState({
    clients: 0,
    totalContacts: 0
  });
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  // Ocultar header cuando modal está abierto en móvil
  useEffect(() => {
    if (showModal) {
      document.body.classList.add('clients-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('clients-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('clients-modal-open');
      document.body.style.overflow = '';
    };
  }, [showModal]);

  // Ocultar header cuando modal de eliminar está abierto en móvil
  useEffect(() => {
    if (showDeleteModal || showBulkDeleteModal) {
      document.body.classList.add('clients-modal-open');
      document.body.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('clients-modal-open');
      document.body.style.overflow = '';
    }

    return () => {
      document.body.classList.remove('clients-modal-open');
      document.body.style.overflow = '';
    };
  }, [showDeleteModal, showBulkDeleteModal]);

  useEffect(() => {
    loadClients();
    loadStatistics();

    // Verificar si se debe abrir el modal desde la URL
    const params = new URLSearchParams(location.search);
    if (params.get('openModal') === 'true') {
      setSelectedClient(null);
      setShowModal(true);
      // Limpiar el parámetro de la URL después de abrir el modal
      window.history.replaceState({}, '', '/clientes');
    }
  }, [location]);

  const loadClients = async () => {
    try {
      setLoading(true);

      // Obtener el companyId de la empresa activa
      const empresaData = localStorage.getItem('empresaActiva');
      let companyId = '3'; // Default: SOBEK INGENIEROS SUPPLY

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          companyId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            companyId = empresaData;
          }
        }
      }

      const response = await api.get('/business-partners/clients', {
        params: {
          search: searchTerm,
          category: selectedCategory,
          companyId: companyId  // Agregar companyId como query parameter
        }
      });
      setClients(response.data.data || []);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStatistics = async () => {
    try {
      // Obtener el companyId de la empresa activa
      const empresaData = localStorage.getItem('empresaActiva');
      let companyId = '3'; // Default: SOBEK INGENIEROS SUPPLY

      if (empresaData) {
        try {
          const parsed = JSON.parse(empresaData);
          companyId = String(parsed.id || parsed.empresaId || '3');
        } catch {
          if (!isNaN(empresaData)) {
            companyId = empresaData;
          }
        }
      }

      const response = await api.get('/business-partners/statistics', {
        params: { companyId: companyId }
      });
      const stats = response.data.data;
      setStatistics({
        clients: stats.clients,
        totalContacts: stats.clientContacts || 0  // Usar solo contactos de clientes
      });
    } catch (error) {
      console.error('Error loading statistics:', error);
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    // Debounce search
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      loadClients();
    }, 500);
  };

  const handleAddClient = () => {
    setSelectedClient(null);
    setShowModal(true);
  };

  const handleEditClient = (client) => {
    setSelectedClient(client);
    setOpenContactsDirectly(false);
    setShowModal(true);
  };

  const handleManageContacts = (client) => {
    setSelectedClient(client);
    setOpenContactsDirectly(true);
    setShowModal(true);
  };

  const handleDeleteClient = async () => {
    try {
      await api.delete(`/business-partners/${clientToDelete.id}`);
      setShowDeleteModal(false);
      setClientToDelete(null);
      loadClients();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting client:', error);
      alert('Error al eliminar el cliente');
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setClientToDelete(null);
  };

  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedClients.length === clients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(clients.map(c => c.id));
    }
  };

  const handleBulkDelete = async () => {
    try {
      // Eliminar cada cliente seleccionado
      await Promise.all(
        selectedClients.map(clientId =>
          api.delete(`/business-partners/${clientId}`)
        )
      );
      setShowBulkDeleteModal(false);
      setSelectedClients([]);
      loadClients();
      loadStatistics();
    } catch (error) {
      console.error('Error deleting clients:', error);
      alert('Error al eliminar los clientes seleccionados');
    }
  };

  const handleCancelBulkDelete = () => {
    setShowBulkDeleteModal(false);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedClient(null);
    setOpenContactsDirectly(false);
    loadClients();
    loadStatistics();
  };

  const getRatingStars = (rating) => {
    return Array(5).fill(0).map((_, index) => (
      <Star
        key={index}
        size={14}
        fill={index < rating ? '#FFD700' : 'none'}
        stroke={index < rating ? '#000' : '#000'}
        strokeWidth={2}
      />
    ));
  };

  const getCategoryBadge = (category) => {
    const categoryLabels = {
      'A': 'Premium',
      'B': 'Estándar',
      'C': 'Básico'
    };
    return categoryLabels[category] ? (
      <span className="category-badge">
        {categoryLabels[category]}
      </span>
    ) : null;
  };

  return (
    <div className="clients-page">
      <div className="page-header">
        <div className="header-content">
          <h1>
            <Building2 size={28} />
            Clientes
          </h1>
          <p className="page-description">
            Gestiona tus clientes y contactos comerciales
          </p>
        </div>

        <div className="header-actions">
          <button className="btn-primary" onClick={handleAddClient}>
            <Plus size={18} />
            Nuevo Cliente
          </button>
        </div>
      </div>

      {selectedClients.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '20px',
          marginTop: '-10px'
        }}>
          <button
            onClick={() => setShowBulkDeleteModal(true)}
            style={{
              backgroundColor: 'white',
              color: '#000',
              border: '2px solid #000',
              padding: '10px 20px',
              borderRadius: '6px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              transition: 'all 0.2s',
              fontFamily: '"Space Grotesk", sans-serif'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#000';
              e.target.style.color = '#c4fffc';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = '#000';
            }}
          >
            <Trash2 size={18} />
            Eliminar {selectedClients.length} seleccionado{selectedClients.length > 1 ? 's' : ''}
          </button>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="statistics-grid">
        <div className="stat-card">
          <div className="stat-icon clients">
            <Building2 size={24} />
          </div>
          <div className="stat-info">
            <h3>{statistics.clients}</h3>
            <p>Total Clientes</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon contacts">
            <Users size={24} />
          </div>
          <div className="stat-info">
            <h3>{statistics.totalContacts}</h3>
            <p>Total Contactos</p>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="filters-section">
        <div className="search-box">
          <Search size={20} />
          <input
            type="text"
            placeholder="Buscar por nombre, RUT..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>

        <div className="filter-controls">
        </div>
      </div>

      {/* Clients List */}
      <div className="clients-grid">
        {loading ? (
          <div className="loading-state">Cargando clientes...</div>
        ) : clients.length === 0 ? (
          <div className="empty-state">
            <Building2 size={48} />
            <h3>No hay clientes registrados</h3>
            <p>Comienza agregando tu primer cliente</p>
            <button className="btn-primary" onClick={handleAddClient}>
              <Plus size={18} />
              Agregar Cliente
            </button>
          </div>
        ) : (
          clients.map(client => (
            <div key={client.id} className="client-card" style={{ position: 'relative' }}>
              <input
                type="checkbox"
                checked={selectedClients.includes(client.id)}
                onChange={() => handleSelectClient(client.id)}
                style={{
                  position: 'absolute',
                  top: '15px',
                  right: '15px',
                  width: '20px',
                  height: '20px',
                  cursor: 'pointer',
                  zIndex: 10,
                  accentColor: '#000'
                }}
                onClick={(e) => e.stopPropagation()}
              />
              <div className="client-header">
                <div className="client-avatar-section">
                  <div className="client-avatar">
                    {(client.tradeName || client.legalName).substring(0, 2).toUpperCase()}
                  </div>
                  <div className="client-main-info">
                    <h3>{client.tradeName || client.legalName}</h3>
                    {client.tradeName && (
                      <p className="legal-name">{client.legalName}</p>
                    )}
                    <p className="tax-id"><strong>RUT: {client.taxId}</strong></p>
                  </div>
                </div>
                <div className="client-badges" style={{ marginTop: '30px' }}>
                  {client.isSupplier === 1 && (
                    <span className="dual-badge">También Proveedor</span>
                  )}
                </div>
              </div>

              <div className="client-info">
                {client.industry && (
                  <div className="info-item">
                    <span className="info-label">Industria:</span>
                    <span>{client.industry}</span>
                  </div>
                )}

                {client.address && (
                  <div className="info-item">
                    <MapPin size={14} />
                    <span>{client.address}, {client.city}</span>
                  </div>
                )}

                {client.phone && (
                  <div className="info-item">
                    <Phone size={14} />
                    <span>{client.phone}</span>
                  </div>
                )}

                {client.email && (
                  <div className="info-item">
                    <Mail size={14} />
                    <span>{client.email}</span>
                  </div>
                )}

                <div className="rating-section">
                  <span>Calificación:</span>
                  <div className="rating-stars">
                    {getRatingStars(client.rating)}
                  </div>
                </div>
              </div>


              <div className="client-actions">
                <button
                  className="btn-action contacts"
                  onClick={() => handleManageContacts(client)}
                  title="Gestionar Contactos"
                >
                  <Mail size={16} />
                  <span className="contacts-count">{client.contacts.length}</span>
                </button>
                <button
                  className="btn-action edit"
                  onClick={() => handleEditClient(client)}
                  title="Editar"
                >
                  <Edit size={16} />
                </button>
                <button
                  className="btn-action delete"
                  onClick={() => {
                    setClientToDelete(client);
                    setShowDeleteModal(true);
                  }}
                  title="Eliminar"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {client.totalSales > 0 && (
                <div className="client-stats">
                  <div className="stat">
                    <span className="stat-label">Ventas Totales:</span>
                    <span className="stat-value">
                      ${client.totalSales.toLocaleString('es-CL')}
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal for Add/Edit Client */}
      {showModal && (
        <BusinessPartnerModal
          isOpen={showModal}
          onClose={handleModalClose}
          partner={selectedClient}
          type="client"
          openContactsDirectly={openContactsDirectly}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && clientToDelete && (
        <div className="modal-overlay delete-modal-custom">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirmar Eliminación</h2>
              <button
                className="modal-close"
                onClick={handleCancelDelete}
                type="button"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="delete-icon">
                  <Trash2 size={48} />
                </div>
                <h3>¿Está seguro de eliminar este cliente?</h3>
                <p>
                  <strong>{clientToDelete.tradeName || clientToDelete.legalName}</strong>
                </p>
                <p className="warning-text">
                  Esta acción no se puede deshacer. Se eliminará el cliente y todos sus contactos asociados.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelDelete}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary-delete"
                onClick={handleDeleteClient}
              >
                <Trash2 size={16} />
                Eliminar Cliente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="modal-overlay delete-modal-custom">
          <div className="modal">
            <div className="modal-header">
              <h2>Confirmar Eliminación Masiva</h2>
              <button
                className="modal-close"
                onClick={handleCancelBulkDelete}
                type="button"
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="delete-confirmation-content">
                <div className="delete-icon">
                  <Trash2 size={48} />
                </div>
                <h3>¿Está seguro de eliminar {selectedClients.length} cliente{selectedClients.length > 1 ? 's' : ''}?</h3>
                <p className="warning-text">
                  Esta acción no se puede deshacer. Se eliminarán todos los clientes seleccionados y sus contactos asociados.
                </p>
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={handleCancelBulkDelete}
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                type="button"
                className="btn-primary-delete"
                onClick={handleBulkDelete}
              >
                <Trash2 size={16} />
                Eliminar {selectedClients.length} Cliente{selectedClients.length > 1 ? 's' : ''}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default ClientsPage;